/**
 * REUSABLE COMPONENT - Loader
 * Generic loading spinner component
 * Can be used in any React project
 */

import React from 'react';

const Loader = ({
  size = 'md',
  variant = 'spinner',
  text,
  fullScreen = false,
  className = '',
}) => {
  // Size classes
  const sizeClasses = {
    xs: 'w-4 h-4',
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16',
  };

  // Spinner component
  const Spinner = () => (
    <svg
      className={`animate-spin ${sizeClasses[size]} text-blue-600`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
  );

  // Dots component
  const Dots = () => (
    <div className="flex space-x-2">
      <div className={`${sizeClasses[size]} rounded-full bg-blue-600 animate-bounce`} style={{ animationDelay: '0ms' }}></div>
      <div className={`${sizeClasses[size]} rounded-full bg-blue-600 animate-bounce`} style={{ animationDelay: '150ms' }}></div>
      <div className={`${sizeClasses[size]} rounded-full bg-blue-600 animate-bounce`} style={{ animationDelay: '300ms' }}></div>
    </div>
  );

  // Pulse component
  const Pulse = () => (
    <div className={`${sizeClasses[size]} rounded-full bg-blue-600 animate-pulse`}></div>
  );

  // Bars component
  const Bars = () => (
    <div className="flex space-x-1 items-end">
      <div className={`w-1 bg-blue-600 animate-pulse`} style={{ height: '24px', animationDelay: '0ms' }}></div>
      <div className={`w-1 bg-blue-600 animate-pulse`} style={{ height: '32px', animationDelay: '150ms' }}></div>
      <div className={`w-1 bg-blue-600 animate-pulse`} style={{ height: '24px', animationDelay: '300ms' }}></div>
    </div>
  );

  // Choose variant
  const LoaderVariant = () => {
    switch (variant) {
      case 'dots':
        return <Dots />;
      case 'pulse':
        return <Pulse />;
      case 'bars':
        return <Bars />;
      default:
        return <Spinner />;
    }
  };

  // Content
  const content = (
    <div className={`flex flex-col items-center justify-center ${className}`}>
      <LoaderVariant />
      {text && <p className="mt-4 text-gray-600">{text}</p>}
    </div>
  );

  // Full screen loader
  if (fullScreen) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white bg-opacity-90">
        {content}
      </div>
    );
  }

  return content;
};

export default Loader;
